import {mountMainHeader} from "@/main/react/header/MainHeader";
import {mountToasterReactComponent} from "@/main/react/header/ToasterReactComponent";
import {mountFloatingActions} from "@/main/react/floating-actions/FloatingActionButtons";
import {mountFooterCookieSettings} from "@/main/react/footer/FooterCookieSettings";
import {LocalizationUtil} from "@/main/util/LocalizationUtil";
import logger from "@/lib/logger";
import { importChunkWithRetryAndReload } from "@/lib/lazyWithRetry";
import { getPageInstanceId, ownsPageInstance } from "@/main/navigation/CurrentPageInstance";
import { isPageExitInProgress } from "@/main/navigation/NavigationState";
import { ensureDeferredCatalogLoaded, ensureSurfaceCatalogLoaded, isCoreCatalogPresent } from "@/main/messages/deferredCatalog";
import { hasMountedReactComponent, markReactComponentMounted, ReactMount } from "@/main/react/mountContract";
import { mountReactNode } from "@/main/react/mounting";
import { RecoverableIslandFallback } from "@/main/react/RecoverableIslandFallback";
import { revealHiddenRecoveryMarkupInPlace } from "@/main/react/ssrSnapshot";
import { captureSsrSnapshot } from "@/main/react/ssrSnapshot";
import React from "react";
import {trackInitialPageContext} from "@/main/analytics/analyticsEventsCore";
import {flushQueuedAnalyticsEvents} from "@/main/analytics/navigationEventQueue";

// Route-level code splitting (designs/01-now/2026-06-08-frontend-bundle-code-splitting-implementation-plan.md
// §5 + 2026-06-11-homepage-cwv-remaining-structural-slices-design.md Slice B): every page-level mount
// is a dynamic import() keyed by data-component, so main_bundle.js no longer ships the code for EVERY
// route on EVERY page (a read page used to eagerly bundle the editor, admin, groups, series, settings,
// newsletter, moderation, metrics, …). Each page now loads only its own chunk + the shared vendor chunk.
//
// Only the genuinely cross-page mounts stay static: the header and the global toaster mount on (almost)
// every page, so keeping them in main_bundle avoids a per-navigation chunk fetch for chrome that is
// always needed, and keeps react-dom in the entry (the lazy chunks import it from there).
const STATIC_MOUNT_MAP: Record<string, ReactMount> = {
    MainHeader: mountMainHeader,
    ToasterReactComponent: mountToasterReactComponent,
    // App-level floating-action overlay: cross-page chrome that survives soft navigation, same
    // rationale as the header/toaster (keep it in the entry bundle, not a per-navigation chunk).
    // See designs/04-live/2026-06-16-app-level-floating-action-buttons-design.md.
    FloatingActions: mountFloatingActions,
    // The footer's "Cookie settings" entry — the consent-withdrawal control both policy documents
    // point at. Static rather than lazy for two reasons: the consent module it delegates to is
    // ALREADY eager (bootstrapFrontEnd imports it at startup), so a chunk would buy a per-page
    // round-trip for code main_bundle already ships; and a lazy island whose container is blank
    // falls back to the RecoverableIslandFallback card below, which inside a footer column is a
    // broken control where an absent one is the correct degradation (rule 32).
    FooterCookieSettings: mountFooterCookieSettings,
};

// data-component -> importer returning { default: mountFn }. Routed through importChunkWithRetryAndReload
// so a transient or stale-bundle chunk failure retries once then reloads once (never a dead island /
// blank page). Adding a new page = one line here + its mount export; no other wiring. Every key must be
// named by a JTE `data-component` literal or a `ClientRouteRegistry` `componentName` (0907#FA-R8,
// `lazyMountMapReachability.spec.ts`): a gate row nothing passes through is dead weight someone maintains.
type LazyMountImporter = () => Promise<{ default: ReactMount }>;

const LAZY_MOUNT_MAP: Record<string, LazyMountImporter> = {
    CreateDiscussionPage: () => import("@/main/react/editor/discussion/CreateDiscussion").then((m) => ({default: m.mountCreateDiscussion})),
    AccountAccessDisplayer: () => import("@/main/react/account_access/AccountAccessDisplayer").then((m) => ({default: m.mountAccountAccessDisplayer})),
    AppAuthResumePage: () => import("@/main/react/account_access/AppAuthResumePage").then((m) => ({default: m.mountAppAuthResumePage})),
    // SV-181 `2a`: the "Continue as @name?" confirm, named by `account_access/app_auth_continue_page.jte`.
    AppAuthContinuePage: () => import("@/main/react/account_access/AppAuthContinuePage").then((m) => ({default: m.mountAppAuthContinuePage})),
    PostSocialLoginPage: () => import("@/main/react/account_access/PostSocialLoginPage").then((m) => ({default: m.mountPostSocialLoginPage})),
    ResetPasswordPage: () => import("@/main/react/account_access/ResetPasswordPage").then((m) => ({default: m.mountResetPasswordPage})),
    SendEmailVerificationPage: () => import("@/main/react/account_access/SendEmailVerificationPage").then((m) => ({default: m.mountSendEmailVerificationPage})),
    Settings: () => import("@/main/react/settings/Settings").then((m) => ({default: m.mountSettings})),
    DiscussionPage: () => import("@/main/react/discussion/DiscussionPage").then((m) => ({default: m.mountDiscussionPage})),
    // Round 100 D1/D2 (SV-155 1b, 2026-10-13): the pending-decision row and the "Get started" checklist are no
    // longer islands of their own above the home tabs; they are notices inside HomePage's feed (HomeFeedList).
    HomePage: () => import("@/main/react/page/HomePage").then((m) => ({default: m.mountHomePage})),
    // FI-2912 (r156): a ROOT of the chat catalog surface (`esbuild.shared.cjs` `DEFERRED_SURFACES`), so the importer
    // also waits for `messages_<locale>_deferred_chat.js`; never rejects, like the deferred wait in `mountLazy`.
    MessagesPage: () => import("@/main/react/page/MessagesPage")
        .then((m) => ensureSurfaceCatalogLoaded("chat").then(() => ({default: m.mountMessagesPage}))),
    MyDashboardPage: () => import("@/main/react/page/MyDashboardPage").then((m) => ({default: m.mountMyDashboardPage})),
    NotificationsPage: () => import("@/main/react/notifications/NotificationsPage").then((m) => ({default: m.mountNotificationsPage})),
    // SV-169 `1b` (2026-10-10): `/conversations`. Named by the backend slice's `conversations/conversations.jte`
    // (`data-component="ConversationsPage"`). Also a client route since r156 (U43, FI-3675: `resolveConversationsRoute`).
    ConversationsPage: () => import("@/main/react/page/ConversationsPage").then((m) => ({default: m.mountConversationsPage})),
    // SV-174 `2a` (r105 round 4): `/reply-session`, named by 1a's `replysession/reply_session.jte`
    // (`data-component="ReplySessionScreen"`); a full document load, not a client route.
    ReplySessionScreen: () => import("@/main/react/replysession/ReplySessionScreen").then((m) => ({default: m.mountReplySessionScreen})),
    UserPage: () => import("@/main/react/page/UserPage").then((m) => ({default: m.mountUserPage})),
    SeriesPage: () => import("@/main/react/page/SeriesPage").then((m) => ({default: m.mountSeriesPage})),
    NewsletterPage: () => import("@/main/react/page/NewsletterPage").then((m) => ({default: m.mountNewsletterPage})),
    NewsletterDashboardPage: () => import("@/main/react/page/NewsletterDashboardPage").then((m) => ({default: m.mountNewsletterDashboardPage})),
    NewsletterDiscoverPage: () => import("@/main/react/page/NewsletterDiscoverPage").then((m) => ({default: m.mountNewsletterDiscoverPage})),
    CreateNewsletterForm: () => import("@/main/react/newsletter/CreateNewsletterForm").then((m) => ({default: m.mountCreateNewsletterForm})),
    NewsletterComposeIssuePage: () => import("@/main/react/newsletter/NewsletterComposeIssuePage").then((m) => ({default: m.mountNewsletterComposeIssuePage})),
    // Newsletter growth D14 (slice 2c): its OWN container in issue.jte, wrapping 1a's server subscribe
    // section and a sibling of the body and the discuss link, never nested in another island (rule 32).
    NewsletterIssueIsland: () => import("@/main/react/newsletter/NewsletterIssueIsland").then((m) => ({default: m.mountNewsletterIssueIsland})),
    GroupPage: () => import("@/main/react/page/GroupPage").then((m) => ({default: m.mountGroupPage})),
    GroupsDirectoryPage: () => import("@/main/react/page/groups-directory/GroupsDirectoryPage").then((m) => ({default: m.mountGroupsDirectoryPage})),
    SearchResultsPage: () => import("@/main/react/page/SearchResultsPage").then((m) => ({default: m.mountSearchResultsPage})),
    TagPage: () => import("@/main/react/page/TagPage").then((m) => ({default: m.mountTagPage})),
    // SV-172 `3a` (r98 round 5): `/news`, named by `news/news.jte` (`data-component="NewsPage"`, around the SSR list
    // only, a sibling of the page heading; rule 32). A full document load, not a client route.
    NewsPage: () => import("@/main/react/page/news/NewsPage").then((m) => ({default: m.mountNewsPage})),
    SubjectPage: () => import("@/main/react/page/SubjectPage").then((m) => ({default: m.mountSubjectPage})),
    ComponentPage: () => import("@/main/react/page/ComponentPage").then((m) => ({default: m.mountComponentPage})),
    DiscussionAccessDeniedPage: () => import("@/main/react/discussion/DiscussionAccessDeniedPage").then((m) => ({default: m.mountDiscussionAccessDeniedPage})),
    // r119 D1 (bet A, FI-2880): `/draft/preview/{token}`, named by `draft/preview.jte` (`data-component="DraftPreviewPage"`,
    // a blank container; rule 32). A full document load, not a client route.
    DraftPreviewPage: () => import("@/main/react/discussion/DraftPreviewPage").then((m) => ({default: m.mountDraftPreviewPage})),
    CustomErrorPage: () => import("@/main/react/error/CustomErrorPage").then((m) => ({default: m.mountCustomErrorPage})),
    ContactUsPage: () => import("@/main/react/contact/ContactUsPage").then((m) => ({default: m.mountContactUsPage})),
    PublishSignupButton: () => import("@/main/react/marketing/PublishSignupButton").then((m) => ({default: m.mountPublishSignupButton})),
    PublishFeatureCarousel: () => import("@/main/react/marketing/PublishFeatureCarousel").then((m) => ({default: m.mountPublishFeatureCarousel})),
    PublishFaqAccordion: () => import("@/main/react/marketing/PublishFaqAccordion").then((m) => ({default: m.mountPublishFaqAccordion})),
    LandingContentShowcase: () => import("@/main/react/marketing/LandingContentShowcase").then((m) => ({default: m.mountLandingContentShowcase})),
    DiscussionMetricsPage: () => import("@/main/metrics/DiscussionMetricsPage").then((m) => ({default: m.mountDiscussionMetricsPage})),
    UserMetricsPage: () => import("@/main/metrics/UserMetricsPage").then((m) => ({default: m.mountUserMetricsPage})),
    GroupMetricsPage: () => import("@/main/metrics/GroupMetricsPage").then((m) => ({default: m.mountGroupMetricsPage})),
    SeriesMetricsPage: () => import("@/main/metrics/SeriesMetricsPage").then((m) => ({default: m.mountSeriesMetricsPage})),
    MemeStudioPage: () => import("@/main/react/meme/MemeStudioPage").then((m) => ({default: m.mountMemeStudioPage})),
    // SV-152 (2026-10-03): the staff support queue, named by `moderation/moderation.jte`. Lazy so staff-only code
    // and its catalog keys stay off the eager graph and the core catalog.
    ModerationPage: () => import("@/main/react/page/moderation/ModerationPage").then((m) => ({default: m.mountModerationPage})),
};

// Stable kebab-case chunk key (scopes the reload-once guard in importChunkWithRetryAndReload).
function chunkKeyForComponent(componentName: string): string {
    return `mount-${componentName}`;
}

// Route components whose chunk is already in memory this session — marked both when a real mount loads
// it and when NavigationPrefetch speculatively warms it, so prefetch never re-imports a chunk that a
// visit (or a prior prefetch) already brought in.
const loadedComponentChunks = new Set<string>();

/** Record that a component's chunk is loaded (called from the real mount path). */
export function markComponentChunkLoaded(componentName: string): void {
    loadedComponentChunks.add(componentName);
}

/**
 * Speculatively warm a route component's JS chunk on navigation intent (hover/press/focus), so a
 * subsequent soft navigation mounts an already-downloaded chunk instead of paying the serial
 * chunk-after-main_bundle round-trip. Best-effort and side-effect-free: it invokes the RAW importer
 * (not importChunkWithRetryAndReload — a speculative hover must never trigger the reload-on-stale-chunk
 * path) and swallows failures. Returns whether it actually *issued* a new import (false = already
 * loaded / unknown component), so callers can count real prefetches for telemetry.
 */
export function prefetchComponentChunk(componentName: string): boolean {
    if (loadedComponentChunks.has(componentName)) {
        return false;
    }
    const importer = LAZY_MOUNT_MAP[componentName];
    if (!importer) {
        return false;
    }
    loadedComponentChunks.add(componentName);
    void importer().catch(() => {
        // Speculative prefetch: on failure, forget it so a later real mount can retry cleanly.
        loadedComponentChunks.delete(componentName);
    });
    return true;
}

export function resetReactMounterPrefetchForTests(): void {
    loadedComponentChunks.clear();
}

/**
 * Mounts every React island under {@code root}. Page-level islands load their chunk on demand, so this
 * is async: the returned promise resolves once all visible islands have mounted (or settled on a chunk
 * error). Callers that gate on real content being on screen — notably the cold-boot splash dismiss in
 * bootstrapFrontEnd — must await it; soft-nav remounts may fire-and-forget (the swapped-in document is
 * already on screen while its chunk loads).
 */
export async function mountAll(root: Document | HTMLElement = document): Promise<void> {
    if (root instanceof Document) {
        try {
            trackInitialPageContext();
        } catch (e) {
            logger.error("ReactMounter: failed to track initial page context", e);
        }

        try {
            flushQueuedAnalyticsEvents();
        } catch (e) {
            logger.error("ReactMounter: failed to flush queued analytics events", e);
        }
    }

    const selector = ".react-component[data-component]";
    const elements: NodeListOf<HTMLElement> = root.querySelectorAll(selector) as NodeListOf<HTMLElement>;

    const pendingMounts: Promise<void>[] = [];
    // Minted before every mountAll (boot: setupEnhancedNavigation; soft paths: remountFrontEndSubtree).
    const scheduledForPageInstance = getPageInstanceId();

    elements.forEach((el) => {
        if (hasMountedReactComponent(el)) {
            return;
        }

        const componentName = el.getAttribute("data-component");
        if (!componentName) return;

        const staticMount = STATIC_MOUNT_MAP[componentName];
        const lazyImporter = LAZY_MOUNT_MAP[componentName];
        if (!staticMount && !lazyImporter) {
            logger.warn(`ReactMounter: no mount function registered for component '${componentName}'`);
            return;
        }

        // Snapshot the server-rendered markup BEFORE anything can replace it, so a failure inside the
        // island can restore it rather than leaving the page with less than the HTML we shipped.
        // See ssrSnapshot.ts for why (crawlers render JS; the "no-JS pass" assumption is not enough).
        //
        // ⚠️ THIS SITS ABOVE THE STATIC BRANCH, AND IT HAS TO (rule 32, 2026-08-24). Until then the call
        // lived in the lazy branch below, which made rule 32's second door a property of being CODE-SPLIT
        // instead of a property of being an ISLAND: MainHeader, ToasterReactComponent, FloatingActions
        // and FooterCookieSettings mounted with no snapshot, so MountErrorBoundary took its `else`
        // branch on every crash of a static island and adding recovery markup to one of their templates
        // did nothing whatsoever. It shipped that way because the boundary's own spec established the
        // capture by hand — a passing test proves nothing if it mocks the failing primitive, and here the
        // primitive was a PRECONDITION. Moving a route into STATIC_MOUNT_MAP for a bundle-budget reason
        // (rule 27) must not quietly cost it its recovery.
        //
        // Unregistered components bail ABOVE this deliberately: a key nothing will ever mount is map
        // noise no consumer can clear, and the map outlives the page (see the 2026-08-11 stale-snapshot
        // direction in AGENTS/frontend-islands-and-ssr.md).
        //
        // Design: designs/01-now/2026-08-24-rule-32-static-island-capture-and-the-degraded-marker.md
        captureSsrSnapshot(componentName, el);

        if (staticMount) {
            try {
                staticMount(el, LocalizationUtil.get);
            } catch (e) {
                logger.error(`ReactMounter: failed to mount ${componentName}`, e);
            }
            return;
        }

        // Claim the element before the chunk resolves so a concurrent mountAll() can't schedule a
        // second mount (which would createRoot() twice on the same node). The mount fn re-marks it
        // idempotently; a failed chunk reloads the page (clearing the DOM) via importChunkWithRetryAndReload.
        markReactComponentMounted(el);
        // The chunk is now being loaded for a real mount — record it so NavigationPrefetch treats this
        // component as already-loaded and never re-imports it on a later hover.
        markComponentChunkLoaded(componentName);

        pendingMounts.push(
            // The route chunk and the DEFERRED half of the locale catalog are fetched in PARALLEL, not
            // in sequence: the catalog holds the copy every lazily-imported island renders (the core
            // half preloaded in <head> covers the eager chrome), and awaiting it after the chunk would
            // cost a second round trip on the first island of every page. It resolves at most once per
            // page load, so every later island reuses the same promise.
            //
            // `ensureDeferredCatalogLoaded` never rejects, so this Promise.all can only fail for the
            // chunk — the chunk recovery below is unchanged. A degraded CATALOG outcome is handled
            // inline, and what it does changed on 2026-09-08 with LAT-8.
            //
            // ── WHY THE BRANCH FLIPPED ────────────────────────────────────────────────────────────
            //
            // Until LAT-8, `main_bundle.js` carried the whole English catalog and
            // `LocalizationUtil.get` fell back to it key by key, so a missing deferred half produced a
            // page partly in English — complete, functional, nothing subtracted. Mounting was
            // therefore the rule-32-correct answer, and the loader's docblock said in as many words
            // that the branch had to flip when the eager English catalog went. It has gone (187 KB off
            // every page in every locale), so a missing half now means RAW KEYS.
            //
            // Raw keys over server-rendered content IS a subtraction: `mountReactNode` uses
            // `createRoot()`, which discards the container's children, so the island would replace a
            // real heading and body with `discussion.section.participants`. So the policy is the same
            // one the chunk-failure door two blocks down already implements, for the same reason:
            //
            //   - the container carries server-rendered content  -> DO NOT MOUNT, keep it;
            //   - the container is blank (a skeleton placeholder) -> mount anyway. There is nothing to
            //     subtract from, and a degraded island beats a frozen skeleton. Core copy is still
            //     correct in the `failed` case, which is every case where a catalog loaded at all.
            //
            // The element is marked EITHER WAY, because both outcomes look completely correct from the
            // outside — a page in the wrong language, or a page that simply kept the HTML it had.
            // Registered in tests/helpers/islandHealthGuard.mjs (rule 32: a recovery path without a
            // marker has quietly deleted coverage).
            Promise.all([
                importChunkWithRetryAndReload(lazyImporter, chunkKeyForComponent(componentName)),
                ensureDeferredCatalogLoaded(),
            ])
                .then(([{default: mountFn}, catalogOutcome]) => {
                    // The page this mount was scheduled for has been navigated away from while its chunk
                    // loaded (a static island's effect can soft-hop before a lazy page chunk arrives). Mounting
                    // now would hand the departed page the NEW page's instance id, and its history-backed
                    // state would rewrite the new page's address (ownership design §15, FI-3288).
                    if (!ownsPageInstance(scheduledForPageInstance)) {
                        return;
                    }
                    // FI-4483: the deferred outcome cannot see a missing CORE half (both halves create
                    // `window.localizedMessages`), so core presence is read beside it, with its own marker.
                    const coreMissing = !isCoreCatalogPresent();
                    if (coreMissing) {
                        el.dataset.islandLocaleCoreMissing = "true";
                    }
                    if (catalogOutcome !== "loaded" || coreMissing) {
                        if (catalogOutcome !== "loaded") {
                            el.dataset.islandLocaleCatalogDegraded = catalogOutcome;
                        }
                        const degradedReason = catalogOutcome !== "loaded" ? catalogOutcome : "core-missing";
                        // FI-6726 (settle r206 A1 ⚑AT1): a `skewed` half LOADED from another build, so only the keys
                        // that build moved are raw. Keeping the markup there left the composer and vote buttons inert
                        // for every page that hit the old task during a rollout, so it mounts (the pre-FI-6700
                        // raw-key degradation), still marked. A missing CORE half still keeps the markup.
                        const mountsDespiteSkew = catalogOutcome === "skewed" && !coreMissing;
                        if (!mountsDespiteSkew && (el.textContent ?? "").trim().length > 0) {
                            // Rule 32's third door, and the same reveal its first door makes: an island
                            // may ship `hidden` recovery-only chrome, and keeping server content nobody
                            // can see is the same defect as deleting it.
                            revealHiddenRecoveryMarkupInPlace(el);
                            logger.warn(
                                `ReactMounter: ${componentName} not mounted — the locale catalog is ` +
                                    `${degradedReason} and mounting would replace server-rendered ` +
                                    "content with untranslated keys; keeping the server markup",
                            );
                            return;
                        }
                        logger.warn(
                            `ReactMounter: mounting ${componentName} over a ` +
                                `${mountsDespiteSkew ? "possibly server-rendered" : "blank"} container with a ` +
                                `${degradedReason} locale catalog; some copy will render as keys`,
                        );
                    }
                    return mountFn(el, LocalizationUtil.get);
                })
                .catch((e) => {
                    // importChunkWithRetryAndReload already retried once and reloaded once; reaching here
                    // means the chunk is genuinely unavailable (e.g. pruned after a deploy). The element
                    // was claimed above but never got a React root.
                    //
                    // The fallback exists "rather than leave a blank container" — so only claim a
                    // container that is ACTUALLY blank. Several islands wrap server-rendered content:
                    // discussion.jte's instant-content branch emits the real <h1>, author link and body
                    // inside data-component="DiscussionPage". Because mountReactNode uses createRoot(),
                    // which discards and rebuilds the element's children, rendering the fallback there
                    // DELETED indexable content and replaced it with "Something went wrong" — so a
                    // crawler indexed the error card instead of the discussion (2026-07-24 audit
                    // finding 10; observed 3×/24h, all Applebot). Skeleton placeholders are all empty
                    // bg-muted divs with no text, so a deferred shell still gets the fallback and no
                    // user is left staring at a frozen skeleton.
                    if ((el.textContent ?? "").trim().length > 0) {
                        // Keep the element detectable now that it no longer renders the fallback that
                        // tests/helpers/islandHealthGuard.mjs keys on. Render-time crashes are
                        // unaffected — those come from MountErrorBoundary inside mountReactNode.
                        el.dataset.islandChunkFailed = "true";
                        // Rule 32's FIRST door needs the same reveal as its second (2026-08-22). An
                        // island may now ship `hidden` recovery-only chrome (main-header-react.jte),
                        // and keeping server content nobody can see is the same defect as deleting it.
                        // MainHeader itself mounts EAGERLY, so this door is not reachable for it today
                        // — the line is here because rule 32 says to enumerate the other doors when you
                        // find one, not to wait for the next island to rediscover the gap.
                        revealHiddenRecoveryMarkupInPlace(el);
                        logger.warn(
                            `ReactMounter: ${componentName} chunk unavailable; keeping server-rendered content`,
                            e,
                        );
                        return;
                    }
                    // r204l (the FI-6604 gap at this door): a hard navigation cancels the old document's
                    // in-flight chunk loads, and `lazyWithRetry` then rejects instead of reloading because the
                    // page is leaving. That is not a chunk failure a member saw, so while the page EXITS
                    // (`isPageExitInProgress()`, FI-6617, board r205 A3) the line goes out at warn and never
                    // carries the `failed to load/mount` text the chunk-load-failure alarm matches. A soft
                    // navigation does NOT count: it mounts the next page's islands inside its own window, so a
                    // real failure there still logs the ERROR line. The fallback below still renders, unchanged.
                    if (isPageExitInProgress()) {
                        logger.warn(`ReactMounter: the ${componentName} chunk did not load while the page was leaving`, e);
                    } else {
                        logger.error(`ReactMounter: failed to load/mount ${componentName} chunk`, e);
                    }
                    // Blank container — render a friendly, manually-retryable fallback instead of a
                    // dead island.
                    try {
                        mountReactNode(el, <RecoverableIslandFallback />);
                    } catch (fallbackError) {
                        logger.error(`ReactMounter: failed to render fallback for ${componentName}`, fallbackError);
                    }
                }),
        );
    });

    await Promise.allSettled(pendingMounts);
}
